import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { toast } from 'sonner';
import {
  Plus, FileText, Download, Trash2, ChevronDown, ChevronUp, Send, ShieldCheck, Copy, CheckCircle2, Loader2
} from 'lucide-react';

const ETAReports = () => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [signEta, setSignEta] = useState(null);  // ÄTA som ska signeras
  const [signForm, setSignForm] = useState({ signer_name: '', signer_email: '' });
  const [signing, setSigning] = useState(false);
  const [signResult, setSignResult] = useState(null);

  const openSignDialog = (eta) => {
    setSignEta(eta);
    setSignForm({ signer_name: '', signer_email: '' });
    setSignResult(null);
  };

  const submitSigning = async () => {
    if (!signForm.signer_name.trim() || !signForm.signer_email.trim()) {
      toast.error('Ange mottagarens namn och e-post');
      return;
    }
    setSigning(true);
    try {
      const res = await api.startEtaSigning(signEta.id, signForm);
      setSignResult(res.data);
      toast.success('Signeringslänk skickad via e-post!');
      // refresh list
      const list = await api.getETAReports();
      setReports(list.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Kunde inte starta signering');
    } finally {
      setSigning(false);
    }
  };

  const copyLink = (url) => {
    navigator.clipboard.writeText(url);
    toast.success('Länk kopierad!');
  };
  const [expandedId, setExpandedId] = useState(null);
  const [form, setForm] = useState({
    project_id: '', employee_id: '', title: '', description: '', date: new Date().toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unit: 'st', unit_price: 0, total: 0 }]
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [etaRes, projRes, empRes] = await Promise.all([
        api.getETAReports({}),
        api.getProjects(),
        api.getEmployees(),
      ]);
      setReports(etaRes.data || []);
      setProjects(projRes.data || []);
      setEmployees(empRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const addItem = () => setForm(f => ({ ...f, items: [...f.items, { description: '', quantity: 1, unit: 'st', unit_price: 0, total: 0 }] }));
  const removeItem = (idx) => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));
  const updateItem = (idx, field, value) => {
    setForm(f => {
      const items = [...f.items];
      items[idx] = { ...items[idx], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        items[idx].total = (parseFloat(items[idx].quantity) || 0) * (parseFloat(items[idx].unit_price) || 0);
      }
      return { ...f, items };
    });
  };

  const totalAmount = form.items.reduce((s, it) => s + (it.total || 0), 0);

  const handleCreate = async () => {
    if (!form.project_id || !form.title) { toast.error('Ange projekt och titel'); return; }
    try {
      await api.createETAReport({
        ...form,
        employee_id: form.employee_id || 'admin',
        total_amount: totalAmount,
      });
      toast.success('ÄTA-rapport skapad!');
      setShowDialog(false);
      setForm({ project_id: '', employee_id: '', title: '', description: '', date: new Date().toISOString().split('T')[0], items: [{ description: '', quantity: 1, unit: 'st', unit_price: 0, total: 0 }] });
      fetchData();
    } catch (err) {
      toast.error('Kunde inte skapa ÄTA-rapport');
      console.error(err);
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.updateETAStatus(id, status);
      toast.success(`Status uppdaterad till ${status === 'sent' ? 'Skickad' : status === 'signed' ? 'Signerad' : status}`);
      fetchData();
    } catch (err) { toast.error('Kunde inte uppdatera status'); }
  };

  const getProjectName = (pid) => projects.find(p => p.id === pid)?.name || pid?.substring(0, 8);
  const statusColors = { unsent: 'bg-yellow-50 text-yellow-700', sent: 'bg-blue-50 text-blue-700', signed: 'bg-green-50 text-green-700' };
  const statusLabels = { unsent: 'Ej skickad', sent: 'Skickad', signed: 'Signerad' };

  if (loading) return <div className="flex items-center justify-center h-32"><div className="w-5 h-5 border-2 border-[hsl(16,100%,55%)] border-t-transparent op-round rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="eta-reports-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>ÄTA-rapporter</h2>
          <p className="text-sm text-muted-foreground mt-1">Ändrings- och tilläggsarbeten</p>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="new-eta-btn">
          <Plus size={14} className="mr-1.5" /> Ny ÄTA-rapport
        </Button>
      </div>

      {reports.length === 0 ? (
        <div className="bg-card border border-border rounded p-8 text-center">
          <FileText size={32} className="text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Inga ÄTA-rapporter. Klicka "Ny ÄTA-rapport" för att skapa.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reports.map((eta) => {
            const isExpanded = expandedId === eta.id;
            return (
              <div key={eta.id} className="bg-card border border-border rounded" data-testid={`eta-${eta.id}`}>
                <div
                  onClick={() => setExpandedId(isExpanded ? null : eta.id)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-muted/30 transition-colors cursor-pointer"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-foreground">{eta.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {getProjectName(eta.project_id)} · {new Date(eta.date).toLocaleDateString('sv-SE')}
                      {eta.total_amount ? ` · ${eta.total_amount.toLocaleString('sv-SE')} kr` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${statusColors[eta.status] || 'bg-muted text-foreground/80'}`}>
                      {statusLabels[eta.status] || eta.status}
                    </span>
                    {eta.status === 'unsent' && (
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleStatusChange(eta.id, 'sent'); }} data-testid={`send-eta-${eta.id}`}>
                        <Send size={12} className="mr-1" /> Skicka
                      </Button>
                    )}
                    {eta.signature_status !== 'signed' && eta.status !== 'signed' && (
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); openSignDialog(eta); }} data-testid={`bankid-sign-eta-${eta.id}`} className="border-[#E8613A]/40 text-[#E8613A] hover:bg-[#E8613A]/5">
                        <ShieldCheck size={12} className="mr-1" /> BankID
                      </Button>
                    )}
                    {eta.status === 'sent' && (
                      <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleStatusChange(eta.id, 'signed'); }} data-testid={`sign-eta-${eta.id}`}>
                        Markera signerad
                      </Button>
                    )}
                    <Button variant="outline" size="sm" onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        const res = await api.downloadEtaPdf(eta.id);
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                        const a = document.createElement('a'); a.href = url; a.download = `ata-${eta.title}.pdf`;
                        document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                      } catch (err) { toast.error('Kunde inte generera PDF'); }
                    }} data-testid={`download-eta-pdf-${eta.id}`}>
                      <Download size={12} className="mr-1" /> PDF
                    </Button>
                    {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="border-t border-border px-5 py-4 space-y-3">
                    <p className="text-sm text-muted-foreground">{eta.description}</p>
                    {(eta.items?.length > 0) && (
                      <table className="w-full text-sm">
                        <thead className="bg-muted/50">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium uppercase text-muted-foreground">Beskrivning</th>
                            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Antal</th>
                            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Enhet</th>
                            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Pris/st</th>
                            <th className="px-3 py-2 text-right text-xs font-medium uppercase text-muted-foreground">Summa</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {eta.items.map((item, i) => (
                            <tr key={i}>
                              <td className="px-3 py-2">{item.description}</td>
                              <td className="px-3 py-2 text-right">{item.quantity}</td>
                              <td className="px-3 py-2 text-right">{item.unit}</td>
                              <td className="px-3 py-2 text-right">{(item.unit_price || 0).toLocaleString('sv-SE')} kr</td>
                              <td className="px-3 py-2 text-right font-medium">{(item.total || 0).toLocaleString('sv-SE')} kr</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr className="bg-muted/30 border-t-2">
                            <td colSpan="4" className="px-3 py-2 font-bold">TOTALT</td>
                            <td className="px-3 py-2 text-right font-bold">{(eta.total_amount || 0).toLocaleString('sv-SE')} kr</td>
                          </tr>
                        </tfoot>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" data-testid="create-eta-dialog">
          <DialogHeader>
            <DialogTitle>Ny ÄTA-rapport</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2 max-h-[65vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Projekt *</Label>
                <select value={form.project_id} onChange={(e) => setForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full h-10 px-3 border border-input rounded text-sm bg-background" data-testid="eta-project-select">
                  <option value="">Välj projekt...</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Datum</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))} data-testid="eta-date" />
              </div>
            </div>
            <div>
              <Label>Titel *</Label>
              <Input value={form.title} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="T.ex. Extra dränering - Villa Eriksson" data-testid="eta-title" />
            </div>
            <div>
              <Label>Beskrivning</Label>
              <Textarea value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Beskriv tilläggsarbetet i detalj..." rows={2} data-testid="eta-description" />
            </div>

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-bold">Poster</Label>
                <Button variant="outline" size="sm" onClick={addItem} data-testid="add-eta-item">
                  <Plus size={12} className="mr-1" /> Lägg till rad
                </Button>
              </div>
              <div className="space-y-2">
                {form.items.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`eta-item-${idx}`}>
                    <div className="col-span-4">
                      {idx === 0 && <Label className="text-xs">Beskrivning</Label>}
                      <Input value={item.description} onChange={(e) => updateItem(idx, 'description', e.target.value)} placeholder="Arbete / material" />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Antal</Label>}
                      <Input type="number" value={item.quantity} onChange={(e) => updateItem(idx, 'quantity', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="col-span-1">
                      {idx === 0 && <Label className="text-xs">Enhet</Label>}
                      <Input value={item.unit} onChange={(e) => updateItem(idx, 'unit', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Pris/st</Label>}
                      <Input type="number" value={item.unit_price} onChange={(e) => updateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)} />
                    </div>
                    <div className="col-span-2">
                      {idx === 0 && <Label className="text-xs">Summa</Label>}
                      <div className="h-10 flex items-center text-sm font-medium">{item.total.toLocaleString('sv-SE')} kr</div>
                    </div>
                    <div className="col-span-1">
                      {form.items.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeItem(idx)} className="text-red-500 hover:text-red-700 p-1">
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-end mt-2 text-sm font-bold">
                Totalt: {totalAmount.toLocaleString('sv-SE')} kr
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Avbryt</Button>
            <Button onClick={handleCreate} className="bg-[hsl(16,100%,55%)] hover:bg-[hsl(16,100%,45%)] text-white" data-testid="create-eta-submit">
              Skapa ÄTA-rapport
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* BankID Sign Dialog */}
      <Dialog open={!!signEta} onOpenChange={(o) => { if (!o) { setSignEta(null); setSignResult(null); } }}>
        <DialogContent className="max-w-md" data-testid="bankid-sign-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck size={18} className="text-[#E8613A]" /> Signera ÄTA med BankID
            </DialogTitle>
          </DialogHeader>
          {!signResult ? (
            <>
              <p className="text-sm text-muted-foreground -mt-2 mb-3">
                Vi skickar en signeringslänk med BankID till mottagaren.
                Länken är giltig i 14 dagar.
              </p>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs">Mottagarens namn *</Label>
                  <Input value={signForm.signer_name} onChange={(e) => setSignForm({ ...signForm, signer_name: e.target.value })} placeholder="Förnamn Efternamn" data-testid="signer-name" />
                </div>
                <div>
                  <Label className="text-xs">Mottagarens e-post *</Label>
                  <Input type="email" value={signForm.signer_email} onChange={(e) => setSignForm({ ...signForm, signer_email: e.target.value })} placeholder="kund@example.se" data-testid="signer-email" />
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setSignEta(null)}>Avbryt</Button>
                <Button onClick={submitSigning} disabled={signing} className="bg-[#E8613A] hover:bg-[#D4532F] text-white" data-testid="confirm-sign-btn">
                  {signing ? <><Loader2 size={14} className="mr-1.5 animate-spin" /> Skapar…</> : <><Send size={14} className="mr-1.5" /> Skicka signeringslänk</>}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-3" data-testid="sign-result">
              <div className="flex items-start gap-3 bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 rounded p-3">
                <CheckCircle2 size={18} className="text-green-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-bold text-green-900 dark:text-green-200">Länk skickad!</p>
                  <p className="text-xs text-green-800 dark:text-green-300 mt-0.5">
                    Mejl skickat till <strong>{signResult.email_sent_to}</strong>. Vi notifierar dig när dokumentet är signerat.
                  </p>
                </div>
              </div>
              <div>
                <Label className="text-xs">Direktlänk (om du vill skicka via SMS/chat):</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={signResult.signing_url} readOnly className="text-xs" />
                  <Button variant="outline" size="sm" onClick={() => copyLink(signResult.signing_url)}>
                    <Copy size={14} />
                  </Button>
                </div>
              </div>
              <DialogFooter className="mt-2">
                <Button onClick={() => { setSignEta(null); setSignResult(null); }} className="bg-[#E8613A] hover:bg-[#D4532F] text-white w-full">Stäng</Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ETAReports;
